<template>
  <div
    ref="barsContainer"
    class="gl-scoring-bar-chart__wrapper"
    :class="{
      'gl-scoring-bar-chart__minimize-bars': minimizeBars,
    }"
  >
    <VueSkeletonLoader
      v-if="loading"
      animation="wave"
      :color="'#bec3d8'"
      :height="20"
      :radius="'2'"
      type="rect"
    />
    <div v-else-if="dataTableResult.length">
      <transition-group
        appear
        name="slide"
      >
        <div
          v-for="(item, i) in dataTableResult"
          :key="i"
          class="risk-exposure__table"
        >
          <div
            :class="classesForRiskExposureRow(item)"
            @click="typeEntityItemClick(item)"
          >
            <div class="risk-exposure__row-badge-wrapper">
              <div class="risk-exposure__row-badge">
                <GlRiskBadge :value="getItemScore(item)" />
                <div
                  :key="formatLabelName(item)"
                  v-popover:tooltip.top="
                    `${capitalizeFirstLetter(formatLabelName(item))}`
                  "
                  class="risk-exposure__row-badge-text"
                  @click.stop="typeEntityItemClick(item)"
                >
                  {{ formatLabelName(item) }}
                </div>
              </div>

              <div class="risk-exposure__values-wrapper">
                <div class="risk-exposure__values">
                  <GlGlobalSlotSwitcher class="risk-exposure__values-switcher">
                    <template #item-0>
                      {{ item.formattedAmount || getFormattedPrice(item) }}
                    </template>
                    <template
                      v-if="item.formattedAmount && getFormattedPrice(item)"
                      #item-1
                    >
                      {{ getFormattedPrice(item) || item.formattedAmount }}
                    </template>
                  </GlGlobalSlotSwitcher>
                  <GlSkeletonAmount type="amount" />
                </div>
                <div
                  v-if="minimizeBars"
                  class="risk-exposure__values-percents"
                >
                  <div v-if="item.generalShare">
                    {{
                      formatShare(
                        roundShare(item.generalShare),
                        !(item.deepDepthShare && item.generalShare),
                      )
                    }}
                  </div>

                  <div v-if="item.deepDepthShare && item.generalShare">
                    +
                  </div>

                  <div v-if="item.deepDepthShare">
                    {{ formatShare(roundShare(item.deepDepthShare)) }}
                  </div>
                  <GlSkeletonAmount type="percent" />
                </div>
              </div>
            </div>
            <div class="risk-exposure__row-info">
              <div class="risk-exposure__row-info-percents">
                <div
                  v-if="item.deepDepthShare"
                  :key="`indirect-${i}`"
                  v-popover:tooltip.top="'Indirect'"
                  class="risk-exposure__row-info-percents-item risk-exposure__row-info-percents-item--indirect"
                  :style="`width: ${calculateWidth(
                    item,
                    item.deepDepthShare * 100,
                    percentsInPixels,
                  )}`"
                  @click.stop="typeEntityItemClick(item)"
                >
                  <span v-if="!minimizeBars">{{
                    formatShare(roundShare(item.deepDepthShare))
                  }}</span>
                  <GlSkeletonAmount
                    v-if="!minimizeBars"
                    type="percent"
                  />
                </div>
                <div
                  v-if="item.generalShare"
                  :key="`direct-${i}`"
                  v-popover:tooltip.top="'Direct'"
                  class="risk-exposure__row-info-percents-item risk-exposure__row-info-percents-item--direct"
                  :style="`width: ${calculateWidth(
                    item,
                    item.generalShare * 100,
                    percentsInPixels,
                  )}`"
                  @click.stop="typeEntityItemClick(item)"
                >
                  <span v-if="!minimizeBars">
                    {{ formatShare(roundShare(item.generalShare)) }}</span>
                  <GlSkeletonAmount
                    v-if="!minimizeBars"
                    type="percent"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="!minimizeBars"
              class="risk-exposure__row-cntrl"
            >
              <img
                alt="arrow"
                class="risk-exposure__row-cntrl-arrow"
                src="@/assets/svg/tools/arrow.svg"
              >
              <img
                alt="close"
                class="risk-exposure__row-cntrl-close"
                src="@/assets/svg/tools/close.svg"
              >
            </div>
          </div>
        </div>
      </transition-group>
    </div>
    <div
      v-else
      class="risk-exposure__empty"
    >
      <div>There is no data yet.</div>
    </div>
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapMutations } from 'vuex'
//Components
import GlGlobalSlotSwitcher from '@/components/switchers/gl-global-slot-switcher'
import VueSkeletonLoader from 'skeleton-loader-vue'
import GlRiskBadge from '@/components/risk/gl-risk-badge'
import GlSkeletonAmount from '@//components/skeleton/gl-skeleton-amount.vue'
// Utils
import { formatShare, roundShare } from '@/utils/sourcesFormatter'
import { findColorByTypeScore, hex2rgba } from '@/utils/cytoskape-ui-rules'
import { formatByPrice } from '@/utils/format-by-price'
import {
  calculateScoreByRiskLevel,
  calculateRiskLevel,
} from '@/utils/risk-formatter'
import { goToEntity } from '@/utils/go-to-route'
// Libs
import _ from 'lodash'
// Mixins
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'
import { capitalizeFirstLetter } from '@/utils/text-formatter'

export default {
  components: {
    VueSkeletonLoader,
    GlGlobalSlotSwitcher,
    GlRiskBadge,
    GlSkeletonAmount,
  },
  mixins: [deviceWidthMixin],
  props: {
    dataTable: {
      type: Array,
      default: () => [],
    },
    pointerItem: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    full: {
      type: Boolean,
      default: false,
    },
    reverse: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    minimizeBars: {
      type: Boolean,
      default: false,
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    essential: {
      type: Boolean,
      default: false,
    },
    sideControl: {
      type: Boolean,
      default: false,
    },
    activePartBar: {
      type: Object,
      default: () => ({}),
    },
    activeExposurePart: {
      type: Object,
      default: () => ({}),
    },
    activeBarItem: {
      type: Object,
      default: () => ({}),
    },
    selectable: {
      type: Boolean,
      default: true,
    },
    changeForSatoshi: {
      type: String,
      default: null,
    },
    exposurePart: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      percentsInPixels: 400,
    }
  },
  computed: {
    ...mapGetters('directions', [
      'getActiveExposurePart',
      'getActiveTypeEntityItem',
      'getActivePartBar',
      'getActivePartBarUof',
      'getActivePartBarFull',
      'getActiveExposurePartUof',
      'getActiveExposurePartFull',
      'getActiveTypeEntityItemUof',
      'getActiveTypeEntityItemFull',
    ]),
    resolveActiveExposurePart() {
      if (this.sideControl) {
        return this.activeExposurePart
      }
      return this.getActiveExposurePart
    },
    resolveActiveTypeEntityItem() {
      if (this.sideControl) {
        return this.activeBarItem
      }
      return this.getActiveTypeEntityItem
    },
    resolveActivePartBar() {
      if (this.sideControl) {
        return this.activePartBar
      }
      return this.getActivePartBar
    },
    dataTableResult() {
      if (Object.keys(this.resolveActiveTypeEntityItem).length) {
        return this.dataTable.filter(
          item =>
            this.formatLabelName(item) ===
            this.formatLabelName(this.resolveActiveTypeEntityItem),
        )
      }
      return !this.full ? this.dataTable.slice(0, this.perPage) : this.dataTable
    },
    isEntityLinksAvailable() {
      return (
        this.resolveActivePartBar.value === 'entity' ||
        this.resolveActivePartBar.value === 'owner'
      )
    },
    findMaxShare() {
      let maxShare = 0

      for (const item of this.dataTable) {
        const shareSum = item?.deepDepthShare + item?.generalShare
        if (shareSum > maxShare) {
          maxShare = shareSum
        }
      }

      return Math.round(maxShare * 100)
    },
    minBarWidth() {
      return this.minimizeBars ? 10 : 60
    },
  },
  watch: {
    resolveActiveExposurePart() {
      this.calculatePercentsInPixels()
    },
  },
  mounted() {
    window.addEventListener('resize', this.calculatePercentsInPixels)
    this.calculatePercentsInPixels()
  },
  destroyed() {
    window.removeEventListener('resize', this.calculatePercentsInPixels)
  },
  methods: {
    capitalizeFirstLetter,
    goToEntity,
    roundShare,
    formatShare,
    findColorByTypeScore,
    hex2rgba,
    formatByPrice,
    calculateScoreByRiskLevel,
    calculateRiskLevel,
    ...mapMutations('directions', [
      'SET_ACTIVE_TYPE_ENTITY_ITEM',
      'SET_ACTIVE_TYPE_ENTITY_ITEM_UOF',
      'SET_ACTIVE_TYPE_ENTITY_ITEM_FULL',
    ]),
    getFormattedPrice(item) {
      return item.amountCur
        ? this.formatByPrice(item.amountCur)
        : item.price
        ? this.formatByPrice(item.price)
        : ''
    },
    visibleEntityId(item) {
      if (this.isPdf) return ''

      if (
        this.resolveActivePartBar.value === 'entity' ||
        this.resolveActivePartBar.value === 'owner'
      ) {
        return item.entityId
      }
    },
    formatLabelName(item) {
      if (
        this.resolveActivePartBar.value === 'type' ||
        this.resolveActivePartBar.value === 'funds.type'
      ) {
        return this.formatLabel(item, 'funds.type')
      }
      if (
        this.resolveActivePartBar.value === 'entity' ||
        this.resolveActivePartBar.value === 'owner'
      ) {
        return this.formatLabel(item, 'owner')
      }
      if (this.resolveActivePartBar.value === 'riskLevel') {
        return this.formatLabel(item, 'riskLevel')
      }
    },
    getItemScore(item, withDefault = false) {
      if (this.resolveActivePartBar.value === 'riskLevel') {
        return this.calculateScoreByRiskLevel(item.riskLevel)
      }

      if (withDefault) return item?.funds?.default ? -1 : item?.funds?.score
      return item?.funds?.score ?? -1
    },
    formatLabel(item, target) {
      return _.get(item, target)
        ? _.get(item, target)
        : _.get(item, 'funds.name') || ''
    },
    isRowActive(item) {
      if (
        (this.resolveActivePartBar.value === 'type' ||
          this.resolveActivePartBar.value === 'funds.type') &&
        this.resolveActiveTypeEntityItem &&
        this.resolveActiveTypeEntityItem.funds &&
        this.resolveActiveTypeEntityItem.funds.type
      ) {
        return item.funds?.type === this.resolveActiveTypeEntityItem.funds?.type
      }
      if (
        (this.resolveActivePartBar.value === 'entity' ||
          this.resolveActivePartBar.value === 'owner') &&
        this.resolveActiveTypeEntityItem &&
        this.resolveActiveTypeEntityItem.owner
      ) {
        return item.owner === this.resolveActiveTypeEntityItem.owner
      }
      if (
        this.resolveActivePartBar.value === 'riskLevel' &&
        this.resolveActiveTypeEntityItem &&
        this.resolveActiveTypeEntityItem.riskLevel
      ) {
        return item.riskLevel === this.resolveActiveTypeEntityItem.riskLevel
      }
    },
    clickOnLabel(item) {
      if (!item) return
      this.goToEntity(item.entityId)
    },

    isOneBar(item) {
      return !item?.deepDepthShare || !item?.generalShare
    },
    calculateWidth(item, percent, percentsInPixels) {
      const lineCof = this.minimizeBars ? 2.1 : 1
      const percentWidth = Math.round(
        ((percentsInPixels * lineCof) / (this.findMaxShare || 1)) * percent,
      )

      const minWidthResult = this.isOneBar(item)
        ? this.minBarWidth * 2 + 2
        : this.minBarWidth

      const finalWidth = percentWidth + minWidthResult

      return `${finalWidth}px`
    },
    classesForRiskExposureRow(item) {
      let classes = [`risk-exposure__row`]

      if (this.selectable) {
        classes.push('risk-exposure__row--selectable')
      }

      if (this.reverse) {
        classes.push('risk-exposure__row--reverse')
      }

      if (this.isRowActive(item)) {
        classes.push('risk-exposure__row--active')
      }

      classes.push(
        `risk-exposure__row--${this.calculateRiskLevel(
          this.getItemScore(item, true),
        )}`,
      )

      return classes
    },
    calculatePercentsInPixels() {
      this.$nextTick(() => {
        const divideCof = this.isPdf ? 2.7 : 2
        this.percentsInPixels =
          this.$refs.barsContainer?.offsetWidth / divideCof -
            this.minBarWidth * 2 || 400
      })
    },
    typeEntityItemClick(item) {
      if (!this.selectable) return
      this.$root.$emit('mouseleave-for-fake-tooltip')

      const setMutation = () => {
        return this.SET_ACTIVE_TYPE_ENTITY_ITEM
      }

      const commit = setMutation()

      if (this.sideControl) {
        return this.$emit('click-bar-item', item)
      }

      if (
        this.resolveActivePartBar.value === 'type' ||
        this.resolveActivePartBar.value === 'funds.type'
      ) {
        if (
          this.resolveActiveTypeEntityItem?.funds?.type === item?.funds?.type
        ) {
          commit({})
        } else {
          commit({ ...item, clickedExposurePart: this.exposurePart })
        }
      }

      const barType = this.resolveActivePartBar.value;
      if (!['entity', 'owner'].includes(barType)) return;

      const activeItem = this.resolveActiveTypeEntityItem;
      const isMatch = (item?.owner && activeItem?.owner === item.owner) || (activeItem?.funds?.type === item?.funds?.type);

      if (isMatch) {
        commit({});
      } else {
        commit({ ...item, clickedExposurePart: this.exposurePart });
      }
    },
  },
}
</script>

<style>
.risk-exposure__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  color: #595959;
  margin-top: 40px;
}
.risk-exposure__row {
  display: flex;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid var(--pale-grey);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.4s ease;
}
.risk-exposure__table:first-child .risk-exposure__row {
  border-top: 1px solid var(--pale-grey);
}
.risk-exposure__row-badge-wrapper {
  flex: 0 0 calc(50% - 44px);
  max-width: calc(50% - 44px);
}
.risk-exposure__row-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

.risk-exposure__row-badge-text {
  font-size: var(--Size-L, 16px);
  font-weight: 600;
  line-height: var(--Line-Height-XL, 24px);
  letter-spacing: var(--Letter-Spacing-XS, -0.5px);
  text-transform: capitalize;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
}

.gl-scoring-bar-chart__wrapper .gl-skeleton-amount__amount {
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px); 
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.risk-exposure__values-switcher {
  display: flex;
  flex-shrink: 0;
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
  text-align: right;
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}
.risk-exposure__values-percents {
  font-size: var(--Size-M, 14px);
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  flex-shrink: 0;
}
.risk-exposure__row-info {
  flex: 0 0 calc(50% + 44px);
  max-width: calc(50% + 44px);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-left: 44px;
  position: relative;
  left: 0;
  transition: left 0.4s ease;
  z-index: 2;
}

.risk-exposure__values-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.risk-exposure__values {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 4px;
  white-space: nowrap;
}

.risk-exposure__row-info-percents {
  display: flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
  font-size: 12px;
}
.risk-exposure__row-info-percents-item {
  max-width: 100% !important;
  padding: 6px 8px;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.3;
  color: var(--dark);
  white-space: nowrap;
}

/* control */
.risk-exposure__row-cntrl {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 28px;
  height: 28px;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 1;
  cursor: pointer;
}
.risk-exposure__row-cntrl-arrow {
  display: block;
}
.risk-exposure__row-cntrl-close {
  display: none;
}

/* colors */
.risk-exposure__row--low .risk-exposure__row-info-percents-item--indirect {
  background-color: var(--Indication-Green);
}
.risk-exposure__row--low .risk-exposure__row-info-percents-item--direct {
  background-color: var(--Success-Main);
  color: var(--white);
}
.risk-exposure__row--medium .risk-exposure__row-info-percents-item--indirect {
  background-color: var(--Indication-Yellow);
}
.risk-exposure__row--medium .risk-exposure__row-info-percents-item--direct {
  background-color: var(--Warning-Main);
}
.risk-exposure__row--high .risk-exposure__row-info-percents-item--indirect {
  background-color: var(--Indication-Red);
}
.risk-exposure__row--high .risk-exposure__row-info-percents-item--direct {
  background-color: var(--Error-Main);
  color: var(--white);
}
.risk-exposure__row--unknown .risk-exposure__row-info-percents-item--indirect {
  background: var(--stripped-gradient-unknown-light);
}
.risk-exposure__row--unknown .risk-exposure__row-info-percents-item--direct {
  background: var(--stripped-gradient-unknown);
}
.risk-exposure__row--pending .risk-exposure__row-info-percents-item--indirect {
  background-color: rgb(186, 201, 255);
}
.risk-exposure__row--pending .risk-exposure__row-info-percents-item--direct {
  background-color: rgb(90, 120, 234);
  color: var(--white);
}

/* active */
.risk-exposure__row.risk-exposure__row--selectable:hover,
.risk-exposure__row--active {
  background-color: rgba(248, 250, 252, 1);
}
.risk-exposure__row.risk-exposure__row--selectable:hover
  .risk-exposure__row-info,
.risk-exposure__row--active .risk-exposure__row-info {
  left: -28px;
}
.risk-exposure__row--active .risk-exposure__row-cntrl-arrow {
  display: none;
}
.risk-exposure__row--active .risk-exposure__row-cntrl-close {
  display: block;
}

/* reverse */
.risk-exposure__row--reverse.risk-exposure__row,
.risk-exposure__row--reverse .risk-exposure__row-info,
.risk-exposure__row--reverse .risk-exposure__row-info-percents {
  flex-direction: row-reverse;
}
.risk-exposure__row--reverse
  .risk-exposure__row-badge-wrapper
  .risk-exposure__row-badge {
  justify-content: end;
}
.risk-exposure__row--reverse .risk-exposure__values-wrapper {
  justify-content: end;
}
.risk-exposure__row--reverse .risk-exposure__row-info {
  padding-left: 0;
  padding-right: 44px;
}
.risk-exposure__row--reverse .risk-exposure__values {
  justify-content: end;
}
.risk-exposure__row--reverse .risk-exposure__row-info-percents {
  text-align: right;
}
.risk-exposure__row--reverse .risk-exposure__row-cntrl {
  right: auto;
  left: 0;
}
.risk-exposure__row.risk-exposure__row--selectable.risk-exposure__row--reverse:hover
  .risk-exposure__row-info,
.risk-exposure__row--reverse.risk-exposure__row--active
  .risk-exposure__row-info {
  left: 28px;
}
.risk-exposure__row--reverse .gl-badge-info {
  flex-direction: row-reverse;
}

/* minimize-bars */
.gl-scoring-bar-chart__minimize-bars .risk-exposure__row {
  flex-direction: column;
  align-items: unset;
  gap: 8px;
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-badge-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 100%;
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row-badge-wrapper
  .risk-exposure__row-badge {
  justify-content: start;
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__values-wrapper {
  justify-content: start;
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-cntrl {
  top: unset;
  bottom: 0;
  transform: translate(0, -8px);
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-info {
  flex: 1;
  max-width: 100%;
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--reverse
  .risk-exposure__row-badge {
  align-items: start;
  width: 100%;
}
.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--reverse
  .gl-badge-info {
  justify-content: start;
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--reverse
  .gl-badge-info {
  flex-direction: row;
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row.risk-exposure__row--selectable:hover
  .risk-exposure__row-info,
.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--active
  .risk-exposure__row-info {
  left: initial;
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-info-percents-item {
  padding: 0;
  height: 12px;
  border-radius: 10px;
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-info {
  padding-left: 0;
  padding-right: 0;
  border-radius: 10px;
  background: rgba(9, 21, 64, 0.04);
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__row-badge-text {
  font-size: var(--Size-M, 14px);
  font-weight: 700;
  line-height: var(--Line-Height-L, 20px);
}

.gl-scoring-bar-chart__minimize-bars .risk-exposure__values-wrapper {
  flex-wrap: wrap;
}
.gl-scoring-bar-chart__minimize-bars .risk-exposure__values-percents {
  justify-content: flex-end;
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--low
  .risk-exposure__row-info-percents-item--indirect {
  background-color: var(--Success-Main);
}
.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--low
  .risk-exposure__row-info-percents-item--direct {
  background-color: var(--Success-Hover);
}
.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--medium
  .risk-exposure__row-info-percents-item--indirect {
  background-color: var(--Warning-Light);
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--high
  .risk-exposure__row-info-percents-item--indirect {
  background-color: var(--Error-Main-Light);
}

.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--unknown
  .risk-exposure__row-info-percents-item--indirect {
  background: var(--Indication-Grey);
}
.gl-scoring-bar-chart__minimize-bars
  .risk-exposure__row--unknown
  .risk-exposure__row-info-percents-item--direct {
  background: var(--Content-Tertiary);
}
</style>
